import BrandLogo from "@/components/BrandLogo";
import { Link } from "react-router-dom";
import { ArrowLeft, ShieldCheck, Lock, Mail, FileText } from "lucide-react";

const LEGAL_NAME = "ADHD Wellness Clinic LLC";
const DBA = "Elevated Psychiatry";
const CONTACT_EMAIL = "danielmattox@elevatingmindspsychiatry.com";
const STATES = "Hawaii (HI) and California (CA)";
const EFFECTIVE_DATE = "February 1, 2026";

function Section({ icon: Icon, title, children }) {
  return (
    <section className="space-y-3">
      <div className="flex items-center gap-2">
        {Icon && <Icon className="w-4 h-4 text-primary" />}
        <h2 className="font-display text-lg font-semibold text-foreground">{title}</h2>
      </div>
      <div className="space-y-2 text-sm text-muted-foreground leading-relaxed">{children}</div>
    </section>
  );
}

export default function Privacy() {
  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-card/60 backdrop-blur sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between gap-3">
          <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground" data-testid="privacy-back-link">
            <ArrowLeft className="w-4 h-4" /> Back
          </Link>
          <BrandLogo className="w-36" testId="privacy-logo" />
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-10" data-testid="privacy-page">
        <div className="mb-10 space-y-3 text-center">
          <div className="inline-flex items-center gap-1.5 bg-accent border border-primary/10 rounded-full px-3 py-1 text-xs font-medium text-primary">
            <ShieldCheck className="w-3.5 h-3.5" /> Privacy Policy
          </div>
          <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight">Your privacy, handled with care.</h1>
          <p className="text-sm text-muted-foreground">Effective {EFFECTIVE_DATE}</p>
        </div>

        <div className="space-y-8">
          <Section title="Who we are" icon={FileText}>
            <p>
              This Privacy Policy describes how <strong>{LEGAL_NAME}</strong>, doing business as <strong>{DBA}</strong>
              (&ldquo;Elevated Psychiatry&rdquo;, &ldquo;we&rdquo;, &ldquo;our&rdquo;, or &ldquo;us&rdquo;), collects, uses,
              and protects your information when you use the Elevated Psychiatry patient portal (the &ldquo;App&rdquo;),
              available on the web and on mobile app stores.
            </p>
            <p>
              We are a licensed psychiatric practice operating in {STATES}, based in Hawaii. The App supports our
              clinical services (appointments, messaging, medication management, journaling, ADHD education, and peer
              community) for our active patients.
            </p>
          </Section>

          <Section title="Information we collect">
            <p><strong>Account information.</strong> Name, email, password (stored as a salted hash), and optional profile photo.</p>
            <p><strong>Health information (PHI).</strong> When you use the App, we process protected health information including appointments, messages with your care team, medication records, refill requests, clinical notes shared by your provider, and documents you upload. This information is covered by HIPAA and the Notice of Privacy Practices of our clinic.</p>
            <p><strong>Self-reported content.</strong> Journal entries, mood check-ins, medication tracker logs, and any content you post in the ADHD Club community forum.</p>
            <p><strong>Device & usage data.</strong> IP address, device/browser identifiers, pages visited, and timestamps, used for security, diagnostics, and to improve the App.</p>
            <p><strong>Push notification tokens</strong> (if you opt in), to deliver alerts about messages, refills, or appointment reminders.</p>
          </Section>

          <Section title="How we use your information" icon={Lock}>
            <ul className="list-disc pl-5 space-y-1">
              <li>To provide clinical services (appointments, messaging, medications, documents).</li>
              <li>To authenticate you, keep your account secure, and prevent abuse (including brute-force lockouts).</li>
              <li>To send you transactional emails (password resets, invitations, account notices) and, with your consent, notifications about messages and appointments.</li>
              <li>To operate the ADHD Club community forum, including moderation of posts you make under a pseudonymous display name you choose.</li>
              <li>To comply with our legal, regulatory, and professional obligations.</li>
            </ul>
            <p>We do <strong>not</strong> sell your personal information. We do <strong>not</strong> use your PHI for advertising, and we do not train third-party AI models on your PHI.</p>
          </Section>

          <Section title="How your information is shared">
            <p>We share information only with the service providers that make the App work. These vendors are contractually bound to protect your data and, where PHI is involved, have signed a HIPAA Business Associate Agreement (BAA) with us:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li><strong>IntakeQ</strong> &mdash; electronic health record, appointments, forms, clinical notes, and client roster.</li>
              <li><strong>Spruce Health</strong> &mdash; two-way secure messaging between you and your care team.</li>
              <li><strong>Keragon</strong> &mdash; HIPAA-aware workflow automation used to bridge messages between the App and Spruce.</li>
              <li><strong>Resend</strong> &mdash; transactional email delivery (password resets, invitations).</li>
              <li><strong>MongoDB Atlas</strong> (and the underlying cloud infrastructure) &mdash; encrypted database hosting.</li>
              <li><strong>OpenAI</strong> &mdash; powers the ADHD Education Assistant. Conversations are general educational questions only; we instruct the model not to retain them for training, and we do not share your identifiable PHI with it.</li>
            </ul>
            <p>We may also disclose information if required by law, to protect the safety of our patients or staff, or in connection with a change of ownership of the practice.</p>
          </Section>

          <Section title="HIPAA & your clinical rights">
            <p>
              Elevated Psychiatry is a HIPAA-covered entity. Your treatment records, provider messages, and other
              PHI in the App are governed by our clinic&rsquo;s Notice of Privacy Practices, which your provider has
              shared with you. You have the right to access, amend, and request an accounting of disclosures of your
              health information. To exercise these rights, email us at <a href={`mailto:${CONTACT_EMAIL}`} className="text-primary underline">{CONTACT_EMAIL}</a>.
            </p>
          </Section>

          <Section title="How long we keep your data">
            <p>We retain your account and clinical records for the duration of the provider-patient relationship and afterward as required by medical-records retention laws in the states we operate in ({STATES}), typically a minimum of seven (7) years. You can ask us to delete account data that is not part of your legal medical record by contacting us.</p>
          </Section>

          <Section title="Security">
            <p>We use industry-standard safeguards including TLS encryption in transit, encryption at rest for databases and backups, salted bcrypt password hashing, brute-force lockouts, JWT-based session tokens, and audit logs for provider actions. No system is perfectly secure, so please protect your login credentials and notify us immediately at <a href={`mailto:${CONTACT_EMAIL}`} className="text-primary underline">{CONTACT_EMAIL}</a> if you suspect unauthorized access.</p>
          </Section>

          <Section title="Your choices">
            <ul className="list-disc pl-5 space-y-1">
              <li><strong>Notifications.</strong> You can enable or disable push and email notifications in your profile settings.</li>
              <li><strong>Forum participation.</strong> Use of the ADHD Club forum is optional and requires a separate HIPAA consent. You choose a pseudonymous display name; your real name and email are never shown to other members.</li>
              <li><strong>Account deletion.</strong> Email us to close your portal account. Your clinical record is retained separately as required by law.</li>
            </ul>
          </Section>

          <Section title="Children">
            <p>The App is not intended for users under 13. Minors (13+) may use the App only with the consent and supervision of a parent or legal guardian who is actively engaged with their care team.</p>
          </Section>

          <Section title="Changes to this policy">
            <p>If we make material changes, we will notify you in the App or by email. Continued use of the App after the effective date constitutes acceptance of the updated policy.</p>
          </Section>

          <Section title="Contact us" icon={Mail}>
            <p>{LEGAL_NAME}, DBA {DBA}</p>
            <p>Based in Hawaii. Services offered in {STATES}.</p>
            <p>Privacy inquiries: <a href={`mailto:${CONTACT_EMAIL}`} className="text-primary underline" data-testid="privacy-contact-email">{CONTACT_EMAIL}</a></p>
          </Section>
        </div>

        <footer className="mt-16 pt-6 border-t border-border text-center text-xs text-muted-foreground">
          &copy; {new Date().getFullYear()} {LEGAL_NAME}. All rights reserved.
        </footer>
      </main>
    </div>
  );
}
